<template>
  <div>
    <v-card>
      <v-toolbar flat>
        <v-toolbar-title class="capitalize">
          {{ $tc('common.organization') }}
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <v-text-field
          v-model="search"
          label="Søg"
          hide-details
          class="mr-4"
          autofocus
          solo
          placeholder="Skriv for at søge"
          dense
          clearable
        />

        <v-menu
          v-model="addMenu"
          left
          :close-on-content-click="false"
          min-width="500"
        >
          <template #activator="{ attrs, on }">
            <v-btn
              v-bind="attrs"
              v-on="on"
            >
              {{ $t('common.add') }}
              <v-icon right> mdi-plus </v-icon>
            </v-btn>
          </template>

          <v-card>
            <v-card-title>
              Ny organisation
            </v-card-title>

            <v-card-text>
              <v-form
                ref="form"
                v-model="valid"
              >
                <v-text-field
                  v-model="newItem.title"
                  label="Navn"
                  :rules="[ v => !!v || 'Udfyld et navn' ]"
                  outlined
                />
                <v-text-field
                  v-model="newItem.initials"
                  label="Initialer"
                  :rules="[ v => !!v || 'Udfyld initialer', v => String(v).length <= 5 || 'Maks. 5 tegn' ]"
                  outlined
                />
                <v-select
                  v-model="newItem.kind"
                  :items="kinds"
                  label="Type"
                  outlined
                />
              </v-form>
            </v-card-text>

            <v-card-actions>
              <v-btn
                text
                @click="clear"
              >
                {{ $t('common.cancel') }}
              </v-btn>

              <v-spacer></v-spacer>

              <v-btn
                :disabled="!valid"
                @click="add"
              >
                {{ $t('common.add') }}
                <v-icon right> mdi-plus </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </v-toolbar>

      <v-card-text>
        <v-data-table
          :items="organizations"
          :headers="headers"
          :loading="loading"
          :sort-by="[ 'active', 'partDetailCount' ]"
          :sort-desc="[ true, true ]"
          :search.sync="search"
          multi-sort
          :items-per-page="-1"
        >
          <template #[`item.actions`]="{ item }">
            <v-btn
              icon
              append
              @click="onEditItem(item)"
            >
              <v-icon> mdi-pencil-outline </v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog
      v-model="editDialog"
      width="800"
    >
      <v-card>
        <v-card-title>
          {{ $t('common.edit') }}
        </v-card-title>
        <v-card-text>
          <v-form
            v-if="editItem"
            ref="editForm"
            v-model="editForm"
          >
            <v-text-field
              v-model="editItem.title"
              label="Navn"
              autofocus
              outlined
              :rules="[ v => !!v || 'Udfyld et navn' ]"
            />
            <v-text-field
              v-model="editItem.initials"
              label="Initialer"
              :rules="[ v => !!v || 'Udfyld initialer', v => String(v).length <= 5 || 'Maks. 5 tegn' ]"
              hint="Skal være unik"
              persistent-hint
              outlined
            />
            <v-autocomplete
              v-model="editItem.kind"
              :items="organizationKinds"
              label="Type"
              item-value="id"
              item-text="title"
              :rules="[ v => !!v || 'Udfyld type' ]"
              clearable
              auto-select-first
              outlined
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />

          <v-btn :disabled="!editForm" :loading="saving" @click="saveItem()">
            {{ $t('common.save') }}
            <v-icon right> mdi-content-save-outline </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'OrganizationList',
  data() {
    return {
      addMenu: false,
      creating: false,
      loading: false,
      saving: false,
      editDialog: false,
      editForm: false,
      editItem: null,
      organizationKinds: [
        'Internal',
        'Contractor',
        'Customer'
      ],
      headers: [
        { text: 'Navn', value: 'title' },
        { text: 'Initialer', value: 'initials' },
        { text: 'Type', value: 'kind' },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ],
      newItem: {
        title: '',
        initials: ''
      },
      kinds: [
        'Internal',
        'Customer',
        'Contractor'
      ],
      organizations: [],
      valid: false,
    }
  },
  computed: {
    search: {
      get() {
        return this.$route.query.search
      },
      set(value) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            search: value ?? undefined
          }
        })
      }
    }
  },
  async mounted() {
    this.fetchData();
  },
  watch: {
    editDialog(value) {
      if (!value) {
        this.editItem = null;
        this.editForm = false;
      }
    }
  },
  methods: {
    add: async function() {
      this.creating = true;

      try {
        const { data } = await this.$api.post('organizations', this.newItem);

        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? `Error creating ${this.$tc('common.organization')}!`;

        this.$notifier.showSuccess({ message: 'Organisationen blev tilføjet' });

        // if (typeof data.model === 'string') {
        //   this.$router.push('/admin/organizations/' + data.model);
        // }
        this.addMenu = false;
        this.fetchData();
      } catch (error) {
        console.error(error);
        this.$notifier.showError({ message: `Der skete en fejl. Prøv venligst igen. (${error})` });
      } finally {
        this.creating = false;
      }
    },
    clear: function() {
      this.addMenu = false;
      this.newItem = {
        title: '',
        aliases: []
      };
    },

    fetchData: async function() {
      try {
        this.loading = true;

        const { data } = await this.$api.get('organizations');

        this.organizations = data;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.loading = false;
      }
    },
    saveItem: async function() {
      this.saving = true;

      const { id, title, initials, kind } = this.editItem;

      try {
        const { data } = await this.$api.put(`organizations/${id}`, {
          title,
          initials,
          kind
        });

        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        this.$notifier.showSuccess({ message: 'Organisation opdateret' });
        this.fetchData();
        this.editDialog = false;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({ message: error });
      } finally {
        this.saving = false;
      }
    },
    onEditItem: function(item) {
      this.editItem = JSON.parse(JSON.stringify(item));
      this.editDialog = true;
    }
  }
}
</script>
